import { Component, DestroyRef, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, concat, forkJoin, of } from 'rxjs';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsComboboxComponent,
  YdsDatePickerComponent,
  YdsSpinnerComponent,
  YdsSwitchComponent,
  YdsTextAreaComponent,
  YdsTextFieldComponent,
  type YdsComboboxOption,
} from '../../../yds-angular';
import {
  defaultEndDate,
  defaultStartDate,
  firstDayOfMonthFilter,
  formatDisplayDate,
  isInvalid,
  lastDayOfMonthFilter,
  startBeforeEndValidator,
  toFormDate,
} from '../../../shared/general-ledger-yds.utils';
import { BookingDefinitionService } from '../bookingdefinition.service';
import { ElementService } from '../element.service';
import { BookingGroupService } from '../../bookinggroup/bookinggroup.service';
import { EmploymentService } from '../../selection/employment/employment.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteBookingDefinitionWithChecks } from '../bookingdefinition-shared';
import { BookingDefinition } from '../../../model/booking-definition.model';
import { BookingOutputCode } from '../../../model/booking-output-code.model';
import { BookingElement } from '../../../model/booking-element.model';
import { Element2 } from '../../../model/element2.model';
import { EmploymentSelection } from '../../../model/employment-selection.model';

const MAX_END_DATE_ISO = '9999-12-31';

/**
 * Largest "Boekingsdefinitie" (BookingDefinition_Name) length the database accepts.
 * The column is nvarchar(50) (EF: BookingDefinition.Name HasMaxLength(50) in the
 * backend's yfokbjContext.cs). A value over 50 is rejected on save, so the
 * maximum is 50.
 */
const MAX_NAME_LENGTH = 50;

@Component({
  selector: 'app-bookingdefinition-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsSpinnerComponent,
    YdsSwitchComponent,
    YdsComboboxComponent,
    YdsDatePickerComponent,
    YdsTextAreaComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './bookingdefinition-editable.component.html',
  styleUrl: './bookingdefinition-editable.component.scss',
})
export class BookingdefinitionEditableComponent {
  readonly isInvalid = isInvalid;
  readonly firstDayOfMonthFilter = firstDayOfMonthFilter;
  readonly lastDayOfMonthFilter = lastDayOfMonthFilter;
  readonly translate = inject(TranslateService);

  private formBuilder = inject(FormBuilder);
  private bookingDefinitionService = inject(BookingDefinitionService);
  private elementService = inject(ElementService);
  private bookingGroupService = inject(BookingGroupService);
  private employmentService = inject(EmploymentService);
  private dialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);

  private readonly detailsCard = viewChild('detailsCard', { read: ElementRef<HTMLElement> });

  /** Id of the booking definition being edited, or null in add mode. */
  readonly bookingDefinitionId = signal<string | null>(null);
  /** Owning booking group, needed when adding and used for navigation. */
  readonly bookingGroupId = signal<string>('');
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);

  /** The last loaded/saved record, used for the read-only view and to revert on cancel. */
  private readonly record = signal<BookingDefinition | null>(null);

  /** Name of the owning booking group, resolved from its id (used in add mode). */
  private readonly bookingGroupNameSignal = signal<string>('');

  /** Employment selections available for the "Persoon selectie" dropdown. */
  private readonly employmentSelections = signal<EmploymentSelection[]>([]);

  readonly detailsEditing = signal(false);

  readonly form: FormGroup = this.formBuilder.group(
    {
      bookingDefinitionName: ['', Validators.required],
      activeIndicator: [true],
      employmentSelectionId: [null as string | null],
      startDate: [defaultStartDate(), Validators.required],
      endDate: [null],
      memoValue: [''],
    },
    { validators: startBeforeEndValidator('startDate', 'endDate') },
  );

  /** Error message shown when the start date is not before the end date. */
  endDateError(): string {
    return this.form.hasError('startBeforeEnd') &&
      (this.form.get('startDate')?.touched || this.form.get('endDate')?.touched)
      ? 'Begindatum moet voor einddatum liggen'
      : '';
  }

  /** Whether we are creating a new booking definition (no id in the route). */
  readonly isAddMode = computed(() => this.bookingDefinitionId() === null);

  // --- Employment selection options ---------------------------------------------

  /** Combobox options for the "Persoon selectie" dropdown, keyed by id. */
  readonly employmentSelectionOptions = computed<YdsComboboxOption[]>(() => {
    const options = this.employmentSelections().map((selection) => ({
      id: selection.employmentSelectionId,
      label: selection.name,
    }));

    // Keep the label of an already-selected selection visible even if it is not
    // returned by getByContext().
    const selectedId = this.record()?.employmentSelectionId;
    if (selectedId && !options.some((o) => o.id === selectedId)) {
      options.push({ id: selectedId, label: selectedId });
    }

    return options;
  });

  // --- View helpers -------------------------------------------------------------

  readonly bookingDefinitionName = computed(() => this.record()?.bookingDefinition_name ?? '');
  readonly bookingGroupName = computed(
    () => this.record()?.bookingGroup_Name || this.bookingGroupNameSignal(),
  );
  readonly activeDisplay = computed(() => Boolean(this.record()?.activeIndicator));

  readonly employmentSelectionDisplay = computed(() => {
    const id = this.record()?.employmentSelectionId;
    if (!id) {
      return 'n.v.t.';
    }
    return this.employmentSelectionOptions().find((o) => o.id === id)?.label ?? id;
  });

  readonly startDateDisplay = computed(() => formatDisplayDate(this.record()?.startDate, 'nl-NL'));

  readonly endDateDisplay = computed(() => {
    const end = this.record()?.endDate;
    return this.isMaxEndDate(end) ? 'n.v.t.' : formatDisplayDate(end, 'nl-NL');
  });

  readonly memoDisplay = computed(() => this.record()?.memo_Value || 'n.v.t.');

  // --- Uitvoercodes card state --------------------------------------------------

  readonly outputCodesEditing = signal(false);
  readonly isSavingOutputCodes = signal(false);

  /** Rows shown in the "Uitvoercodes" grid (working copy, persisted on save). */
  readonly outputCodeRows = signal<BookingOutputCode[]>([]);

  /** Ids of existing output codes removed during edit, deleted on save. */
  private removedOutputCodeIds: string[] = [];

  /** Output code elements available for selection. */
  private readonly outputCodeElements = signal<Element2[]>([]);

  /** Combobox options showing "outputCode - element name", keyed by element id. */
  readonly outputCodeElementOptions = computed<YdsComboboxOption[]>(() => {
    const options = this.outputCodeElements().map((e) => ({
      id: e.elementId,
      label: `${e.outputCode} - ${e.elementName}`,
    }));
    const ids = new Set(options.map((o) => o.id));

    // Keep the label of already-selected rows visible even if the element is not
    // returned by getOutputCodeElements().
    for (const row of this.outputCodeRows()) {
      if (row.elementId && !ids.has(row.elementId)) {
        ids.add(row.elementId);
        options.push({ id: row.elementId, label: row.elementName ?? row.elementId });
      }
    }

    return options;
  });

  /**
   * Combobox options for a specific row. Output codes already chosen in other
   * rows are filtered out so each uitvoercode can only be selected once. The
   * row's own current selection is always kept visible.
   */
  availableOutputCodeOptions(currentIndex: number): YdsComboboxOption[] {
    const rows = this.outputCodeRows();
    const usedElsewhere = new Set(
      rows
        .filter((_, i) => i !== currentIndex)
        .map((row) => row.elementId)
        .filter((elementId): elementId is string => !!elementId),
    );

    return this.outputCodeElementOptions().filter((option) => !usedElsewhere.has(option.id));
  }

  /** Display label for a row in view mode ("outputCode - element name" when known). */
  outputCodeRowLabel(row: BookingOutputCode): string {
    return (
      this.outputCodeElementOptions().find((o) => o.id === row.elementId)?.label ??
      row.elementName ??
      row.elementId
    );
  }

  // --- Elementen card state -----------------------------------------------------

  readonly elementenEditing = signal(false);
  readonly isSavingElementen = signal(false);

  /** Rows shown in the "Elementen" grid (working copy, persisted on save). */
  readonly elementRows = signal<BookingElement[]>([]);

  /** Ids of existing booking elements removed during edit, deleted on save. */
  private removedBookingElementIds: string[] = [];

  /** Numeric elements available for selection in the "Elementen" card. */
  private readonly numericElements = signal<Element2[]>([]);

  /** Combobox options showing "Rubric - Description" (rubric comes from the
   *  Element master data), keyed by element id. Falls back to the name only when
   *  the element has no rubric. Searchable on either part (the combobox filters
   *  on the label text). */
  readonly elementOptions = computed<YdsComboboxOption[]>(() => {
    const options = this.numericElements().map((e) => ({
      id: e.elementId,
      label: e.rubric ? `${e.rubric} - ${e.elementName}` : e.elementName,
    }));
    const ids = new Set(options.map((o) => o.id));

    // Keep the label of already-selected rows visible even if the element is not
    // returned by getNumericElements().
    for (const row of this.elementRows()) {
      if (row.elementId && !ids.has(row.elementId)) {
        ids.add(row.elementId);
        options.push({ id: row.elementId, label: row.elementName ?? row.elementId });
      }
    }

    return options;
  });

  /**
   * Combobox options for a specific row. Elements already chosen in other rows
   * are filtered out so each element can only be selected once. The row's own
   * current selection is always kept visible.
   */
  availableElementOptions(currentIndex: number): YdsComboboxOption[] {
    const rows = this.elementRows();
    const usedElsewhere = new Set(
      rows
        .filter((_, i) => i !== currentIndex)
        .map((row) => row.elementId)
        .filter((elementId): elementId is string => !!elementId),
    );

    return this.elementOptions().filter((option) => !usedElsewhere.has(option.id));
  }

  /** Display label for a row in view mode (element name when known). */
  elementRowLabel(row: BookingElement): string {
    return (
      this.elementOptions().find((o) => o.id === row.elementId)?.label ??
      row.elementName ??
      row.elementId
    );
  }

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');

    this.loadEmploymentSelections();

    if (id) {
      this.bookingDefinitionId.set(id);
      this.loadBookingDefinition(id);
      this.loadOutputCodeElements();
      this.loadOutputCodes(id);
      this.loadNumericElements();
      this.loadBookingElements(id);
    } else {
      // Add mode: start directly in edit state. The owning booking group is passed
      // through router navigation state by the list component.
      const state = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
      const groupId = (state?.['bookingGroupId'] as string) ?? '';
      this.bookingGroupId.set(groupId);
      if (groupId) {
        this.loadBookingGroupName(groupId);
      }
      this.detailsEditing.set(true);
    }
  }

  private loadBookingGroupName(groupId: string): void {
    this.bookingGroupService
      .getBookingGroupById(groupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((group) => this.bookingGroupNameSignal.set(group?.bookingGroup_name ?? ''));
  }

  private loadEmploymentSelections(): void {
    this.employmentService
      .getByContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selections) => this.employmentSelections.set(selections));
  }

  private loadBookingDefinition(id: string): void {
    this.isLoading.set(true);
    this.bookingDefinitionService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (definition) => {
          if (!definition) {
            this.notFound.set(true);
          } else {
            this.record.set(definition);
            this.bookingGroupId.set(definition.bookingGroupId);
            this.patchForm(definition);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private loadOutputCodeElements(): void {
    this.elementService
      .getOutputCodeElements()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((elements) => this.outputCodeElements.set(elements));
  }

  private loadOutputCodes(id: string): void {
    this.elementService
      .getByBookingDefinition(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => this.outputCodeRows.set(rows));
  }

  private loadNumericElements(): void {
    this.elementService
      .getNumericElements()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((elements) => this.numericElements.set(elements));
  }

  private loadBookingElements(id: string): void {
    this.elementService
      .getBookingElementsByBookingDefinition(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => this.elementRows.set(rows));
  }

  private patchForm(definition: BookingDefinition): void {
    this.form.patchValue({
      bookingDefinitionName: definition.bookingDefinition_name,
      activeIndicator: definition.activeIndicator,
      employmentSelectionId: definition.employmentSelectionId ?? null,
      startDate: toFormDate(definition.startDate),
      endDate: this.isMaxEndDate(definition.endDate) ? null : toFormDate(definition.endDate),
      memoValue: definition.memo_Value ?? '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private isMaxEndDate(value: unknown): boolean {
    const date = toFormDate(value as Date | string | null);
    if (!date) {
      return false;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` === MAX_END_DATE_ISO;
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForm(current);
    }
    this.detailsEditing.set(false);
    this.scrollToDetails();
  }

  onDetailsNext(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value as {
      bookingDefinitionName: string;
      activeIndicator: boolean;
      employmentSelectionId: string | null;
      startDate: Date | null;
      endDate: Date | null;
      memoValue: string;
    };

    if ((value.bookingDefinitionName?.length ?? 0) > MAX_NAME_LENGTH) {
      this.dialogService.open({
        width: '320px',
        title: 'Tekst te lang',
        message: `De boekingsdefinitie mag maximaal ${MAX_NAME_LENGTH} tekens bevatten.`,
        confirmText: 'Ok',
        variant: 'warning',
      });
      return;
    }

    const current = this.record();

    const payload: BookingDefinition = {
      bookingDefinitionId: this.bookingDefinitionId() ?? '',
      bookingGroupId: this.bookingGroupId(),
      bookingDefinition_name: value.bookingDefinitionName,
      activeIndicator: value.activeIndicator,
      wagePartitioningIndicator: current?.wagePartitioningIndicator ?? false,
      employmentSelectionId: value.employmentSelectionId || null,
      startDate: (value.startDate ?? defaultStartDate()) as Date,
      endDate: (value.endDate ?? defaultEndDate()) as Date,
      memo_Value: value.memoValue ?? '',
      bookingGroup_Name: current?.bookingGroup_Name || this.bookingGroupNameSignal(),
    };

    this.isSaving.set(true);

    const id = this.bookingDefinitionId();
    const save$ = id
      ? this.bookingDefinitionService.updateBookingDefinition(id, payload)
      : this.bookingDefinitionService.addBookingDefinition(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        if (id) {
          this.record.set({ ...payload, ...saved });
          this.detailsEditing.set(false);
          this.scrollToDetails();
        } else if (saved?.bookingDefinitionId) {
          this.router.navigate(['/config/bookingdefinition/edit', saved.bookingDefinitionId]);
        } else {
          this.goBack();
        }
      },
      error: () => {
        this.isSaving.set(false);
      },
    });
  }

  // --- Uitvoercodes card actions ------------------------------------------------

  addOutputCodeRow(): void {
    const newRow: BookingOutputCode = {
      bookingOutputCodeId: '',
      bookingDefinitionId: this.bookingDefinitionId() ?? '',
      elementId: '',
      elementName: '',
    };
    this.outputCodeRows.update((rows) => [...rows, newRow]);
  }

  removeOutputCodeRow(index: number): void {
    const rows = this.outputCodeRows();
    const row = rows[index];
    if (row?.bookingOutputCodeId) {
      this.removedOutputCodeIds.push(row.bookingOutputCodeId);
    }
    this.outputCodeRows.set(rows.filter((_, i) => i !== index));
  }

  onOutputCodeElementChange(index: number, elementId: string | null): void {
    // Clearing the selection leaves the row empty; it is treated as a delete on save.
    const element = elementId
      ? this.outputCodeElements().find((e) => e.elementId === elementId)
      : undefined;
    this.outputCodeRows.update((rows) =>
      rows.map((row, i) =>
        i === index
          ? { ...row, elementId: elementId ?? '', elementName: element?.elementName ?? '' }
          : row,
      ),
    );
  }

  onOutputCodesCancel(): void {
    this.removedOutputCodeIds = [];
    const id = this.bookingDefinitionId();
    if (id) {
      this.loadOutputCodes(id);
    }
    this.outputCodesEditing.set(false);
  }

  onOutputCodesSave(): void {
    const id = this.bookingDefinitionId();
    if (!id) {
      return;
    }

    this.isSavingOutputCodes.set(true);

    const deleteOperations: Observable<unknown>[] = [];
    const updateOperations: Observable<unknown>[] = [];
    const addOperations: Observable<unknown>[] = [];

    this.removedOutputCodeIds.forEach((outputCodeId) =>
      deleteOperations.push(this.elementService.removeBookingOutputCode(outputCodeId)),
    );

    this.outputCodeRows().forEach((row) => {
      // An empty (unselected) uitvoercode is treated as a delete: remove it when
      // it already exists, otherwise simply skip the unsaved row.
      if (!row.elementId) {
        if (row.bookingOutputCodeId) {
          deleteOperations.push(
            this.elementService.removeBookingOutputCode(row.bookingOutputCodeId),
          );
        }
        return;
      }

      const outputCode: BookingOutputCode = {
        ...row,
        bookingDefinitionId: id,
      };
      if (row.bookingOutputCodeId) {
        updateOperations.push(
          this.elementService.updateBookingOutputCode(row.bookingOutputCodeId, outputCode),
        );
      } else {
        addOperations.push(this.elementService.addBookingOutputCode(outputCode));
      }
    });

    const save$: Observable<unknown> = this.runInOrder(
      deleteOperations,
      updateOperations,
      addOperations,
    );

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSavingOutputCodes.set(false);
        this.removedOutputCodeIds = [];
        this.outputCodesEditing.set(false);
        this.loadOutputCodes(id);
      },
      error: () => {
        this.isSavingOutputCodes.set(false);
      },
    });
  }

  // --- Elementen card actions ---------------------------------------------------

  addElementRow(): void {
    const newRow: BookingElement = {
      bookingElementId: '',
      bookingDefinitionId: this.bookingDefinitionId() ?? '',
      elementId: '',
      elementName: '',
    };
    this.elementRows.update((rows) => [...rows, newRow]);
  }

  removeElementRow(index: number): void {
    const rows = this.elementRows();
    const row = rows[index];
    if (row?.bookingElementId) {
      this.removedBookingElementIds.push(row.bookingElementId);
    }
    this.elementRows.set(rows.filter((_, i) => i !== index));
  }

  onElementChange(index: number, elementId: string | null): void {
    // Clearing the selection leaves the row empty; it is treated as a delete on save.
    const element = elementId
      ? this.numericElements().find((e) => e.elementId === elementId)
      : undefined;
    this.elementRows.update((rows) =>
      rows.map((row, i) =>
        i === index
          ? { ...row, elementId: elementId ?? '', elementName: element?.elementName ?? '' }
          : row,
      ),
    );
  }

  onElementenCancel(): void {
    this.removedBookingElementIds = [];
    const id = this.bookingDefinitionId();
    if (id) {
      this.loadBookingElements(id);
    }
    this.elementenEditing.set(false);
  }

  onElementenSave(): void {
    const id = this.bookingDefinitionId();
    if (!id) {
      return;
    }

    this.isSavingElementen.set(true);

    const deleteOperations: Observable<unknown>[] = [];
    const updateOperations: Observable<unknown>[] = [];
    const addOperations: Observable<unknown>[] = [];

    this.removedBookingElementIds.forEach((bookingElementId) =>
      deleteOperations.push(this.elementService.removeBookingElement(bookingElementId)),
    );

    this.elementRows().forEach((row) => {
      // An empty (unselected) element is treated as a delete: remove it when it
      // already exists, otherwise simply skip the unsaved row.
      if (!row.elementId) {
        if (row.bookingElementId) {
          deleteOperations.push(this.elementService.removeBookingElement(row.bookingElementId));
        }
        return;
      }

      const element: BookingElement = {
        ...row,
        bookingDefinitionId: id,
      };
      if (row.bookingElementId) {
        updateOperations.push(
          this.elementService.updateBookingElement(row.bookingElementId, element),
        );
      } else {
        addOperations.push(this.elementService.addBookingElement(element));
      }
    });

    const save$: Observable<unknown> = this.runInOrder(
      deleteOperations,
      updateOperations,
      addOperations,
    );

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSavingElementen.set(false);
        this.removedBookingElementIds = [];
        this.elementenEditing.set(false);
        this.loadBookingElements(id);
      },
      error: () => {
        this.isSavingElementen.set(false);
      },
    });
  }

  /**
   * Runs the given operation groups sequentially: all deletes first, then all
   * updates, and finally all adds. Operations within a group run in parallel.
   */
  private runInOrder(...groups: Observable<unknown>[][]): Observable<unknown> {
    const phases = groups
      .filter((group) => group.length > 0)
      .map((group) => forkJoin(group));
    return phases.length ? concat(...phases) : of(null);
  }

  // --- Page actions -------------------------------------------------------------

  private scrollToDetails(): void {
    setTimeout(() => {
      const element = this.detailsCard()?.nativeElement;
      if (!element) {
        return;
      }

      const chrome = this.document.querySelector('.app-chrome') as HTMLElement | null;
      const offset = chrome?.offsetHeight ?? 0;
      const top = element.getBoundingClientRect().top + window.scrollY - offset - 16;

      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  goBack(): void {
    const groupId = this.bookingGroupId();
    this.router.navigateByUrl(
      groupId ? `config/bookingdefinition/${groupId}` : 'config/bookingdefinition',
    );
  }

  goToBookingGroup(): void {
    this.router.navigate(['/config/bookinggroup/editable', this.bookingGroupId()]);
  }

  goToBookingRule(): void {
    this.router.navigate(['/config/bookingrule', this.bookingGroupId()]);
  }

  onDelete(): void {
    const id = this.bookingDefinitionId();
    if (!id) {
      return;
    }

    deleteBookingDefinitionWithChecks({
      id,
      service: this.bookingDefinitionService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.goBack(),
    });
  }
}
